import type { YachtCategory, YachtGameState } from "@cloudnest/yacht-engine";
import { isYachtCategory } from "@cloudnest/yacht-engine";

export type YachtReplayEvent =
  | {
      readonly eventType: "init";
      readonly userId: string;
      readonly payload: { readonly state: YachtGameState };
    }
  | {
      readonly eventType: "SyncViewerState";
      readonly userId: string;
      readonly payload?: { readonly actionId?: string };
    }
  | {
      readonly eventType: "ROLL_DICE";
      readonly userId: string;
      readonly payload: {
        readonly sheetRound: number;
        readonly turnNumber: number;
      };
    }
  | {
      readonly eventType: "TOGGLE_HOLD";
      readonly userId: string;
      readonly payload: {
        readonly sheetRound: number;
        readonly turnNumber: number;
        readonly dieIndex: number;
      };
    }
  | {
      readonly eventType: "SCORE_CATEGORY";
      readonly userId: string;
      readonly payload: {
        readonly sheetRound: number;
        readonly turnNumber: number;
        readonly category: YachtCategory;
      };
    };

export type YachtReplayEventType = YachtReplayEvent["eventType"];

export interface YachtEventInput {
  readonly eventType: string;
  readonly userId?: string;
  readonly payload?: Record<string, unknown>;
}

export const YACHT_REPLAY_EVENT_TYPES = [
  "init",
  "SyncViewerState",
  "ROLL_DICE",
  "TOGGLE_HOLD",
  "SCORE_CATEGORY",
] as const satisfies readonly YachtReplayEventType[];

function requireUserId(event: YachtEventInput): string {
  if (typeof event.userId === "string" && event.userId.length > 0) {
    return event.userId;
  }
  throw new Error("Yacht replay event is missing userId");
}

function requireInteger(payload: Record<string, unknown>, key: string): number {
  const value = payload[key];
  if (typeof value === "number" && Number.isInteger(value)) return value;
  throw new Error(`Yacht replay event is missing integer payload.${key}`);
}

function requireActionWindow(payload: Record<string, unknown>): {
  readonly sheetRound: number;
  readonly turnNumber: number;
} {
  return {
    sheetRound: requireInteger(payload, "sheetRound"),
    turnNumber: requireInteger(payload, "turnNumber"),
  };
}

function requireCategory(payload: Record<string, unknown>): YachtCategory {
  const value = payload.category;
  if (isYachtCategory(value)) return value;
  throw new Error("Yacht SCORE_CATEGORY replay event is missing category");
}

export function normalizeYachtReplayEventType(
  eventType: string,
): YachtReplayEventType {
  switch (eventType) {
    case "init":
    case "SyncViewerState":
    case "ROLL_DICE":
    case "TOGGLE_HOLD":
    case "SCORE_CATEGORY":
      return eventType;
    default:
      throw new Error(`Unsupported Yacht event type: ${eventType}`);
  }
}

export function toYachtReplayEvent(event: YachtEventInput): YachtReplayEvent {
  const eventType = normalizeYachtReplayEventType(event.eventType);
  const userId = requireUserId(event);
  const payload = event.payload ?? {};
  const actionId =
    typeof payload.actionId === "string" ? payload.actionId : undefined;

  switch (eventType) {
    case "init":
      return {
        eventType,
        userId,
        payload: payload as { readonly state: YachtGameState },
      };
    case "SyncViewerState":
      return {
        eventType,
        userId,
        payload: actionId ? { actionId } : undefined,
      };
    case "ROLL_DICE":
      return {
        eventType,
        userId,
        payload: requireActionWindow(payload),
      };
    case "TOGGLE_HOLD":
      return {
        eventType,
        userId,
        payload: {
          ...requireActionWindow(payload),
          dieIndex: requireInteger(payload, "dieIndex"),
        },
      };
    case "SCORE_CATEGORY":
      return {
        eventType,
        userId,
        payload: {
          ...requireActionWindow(payload),
          category: requireCategory(payload),
        },
      };
  }
}
